import omit from 'lodash/omit';
import {useState} from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from "@mui/material/Grid";
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import FormInput from '../common/FormInput';
import {useCreateLeadMutation, useUpdateLeadMutation} from '../../redux/services/leadApi';
import { 
  getLeadStatusOptions,
  getMarketingStrategyOptions,
  getReferralSourceCategories,
} from '../../helpers/selectOptions';

const getFields = () => [
  {
    name: 'status',
    label: 'Stage',
    type: 'select',
    validate: () => true,
    fullWidth: true,
    required: true,
    options: getLeadStatusOptions(),
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'provider_id',
    label: 'Assignee',
    type: 'selectProvider',
    editableAfterCreate: false,
    required: true,
    fullWidth: true,
    helperText: null,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_first_name',
    label: 'First Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_last_name',
    label: 'Last Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_email',
    label: 'Email',
    type: 'email',
    editableAfterCreate: true,
    required: true,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'enrolled_in_emails',
    label: 'Enroll in Automatic Emails to Encourage Signing Up?',
    type: 'boolean',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'contact_phone',
    label: 'Phone',
    type: 'phone',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'patient_id',
    label: 'Linked Patient',
    type: 'selectPatient',
    editableAfterCreate: false,
    required: true,
    fullWidth: true,
    helperText: 'Link lead to the patient once they have registered',
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'address',
    label: 'Address',
    type: 'address',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referral_category',
    label: 'Referral Category',
    type: 'select',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    options: getReferralSourceCategories().map(c => ({value: c.value.toUpperCase(), label: c.label})),
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referrer_name',
    label: 'Referrer Name',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'referrer_company',
    label: 'Referrer Company',
    type: 'text',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'marketing_strategies',
    label: 'Marketing Strategies',
    type: 'select',
    editableAfterCreate: true,
    required: false,
    fullWidth: true,
    validate: () => true,
    options: getMarketingStrategyOptions(),
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'why_lost',
    label: 'If lost lead, why?',
    type: 'textarea',
    rows: 3,
    fullWidth: true,
    editableAfterCreate: true,
    required: false,
    validate: () => true,
    grid: {
      xs: 12,
      md: 6,
    },
  },
  {
    name: 'note',
    label: 'Notes',
    editableAfterCreate: true, 
    required: false,
    type: 'textarea',
    rows: 5,
    fullWidth: true,
    validate: () => true,
    grid: {
      xs: 12,
      md: 12,
    },
  },
];

const handleFieldUpdate = ({formValues, setFormValues}) => name => value => {
  setFormValues({...formValues, [name]: value});
};

const parseFormValues = formValues => {
  let cleanData = omit(formValues, 'address');
  if (formValues.address) {
    cleanData = {
      ...cleanData,
      contact_address_line_1: formValues.address.line1,
      contact_address_line_2: formValues.address.line2,
      contact_city: formValues.address.city?.value,
      contact_state: formValues.address.state?.value,
      contact_zip: formValues.address.zip,
    };
  }
  return cleanData;
};

const handleSubmit = ({
  id, 
  editable, 
  sendRequest, 
  afterEdit,
  setSubmitting,
  setError,
}) => async (e, formValues) => {
  setSubmitting(true);
  const body = parseFormValues(formValues);
  const params = editable ? {id, body} : body;
  const request = await sendRequest(params);
  setSubmitting(false);
  if (request.error) {
    // TODO: handle error
  }
  if (afterEdit) afterEdit();
};

const LeadForm = props => {
  const {lead: l, editing} = props;
  const fields = getFields();
  const [submitting, setSubmitting] = useState(false);
  const [formValues, setFormValues] = useState(
    editing ? {...l} : {status: 'NEW'}
  );
  const [putLead] = useUpdateLeadMutation();
  const [postLead] = useCreateLeadMutation();
  const onSubmit = handleSubmit({
    id: editing ? l.id : null,
    editing: editing,
    sendRequest: editing ? putLead : postLead,
    afterEdit: props.afterEdit,
  });
  return (
    <Box sx={{width: '100%'}}>
      <Stack spacing={1}>
        <Typography variant='h2' align='center'>Lead Details</Typography>
        <Grid container spacing={2} direction={{xs: 'row'}} padding={2}>
          {fields.map(f =>
            <Grid item xs={f.grid?.xs ?? 12} sm={f.grid?.sm} md={f.grid?.md}>
              <FormInput
                defaultValue={l?.[f.name]}
                onChange={handleFieldUpdate({formValues, setFormValues})(f.name)}
                formValues={formValues}
                {...f}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <Stack alignItems='center'>
              <Box sx={{width: 'fit-content'}}>
                <Button
                  variant='primaryGradient'
                  disabled={submitting}
                  onClick={e => onSubmit(e, formValues)}
                >
                  Save Changes
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

LeadForm.propTypes = {
  editable: PropTypes.bool,
  lead: PropTypes.object,
}

export default LeadForm;